import z from 'zod'
import { VoiceOption } from '../models/VoiceOption'
import { SlideDeckMaterialType } from '../firestore/SlideDeckMaterial/schema'
import { SlideDeckMaterialWorkType } from '../firestore/SlideDeckMaterial/schema'
import { SlideType } from '../firestore/Slide/types'
import { RubricType } from '../firestore/PromptRubricType/types'

// Custom schema for image URLs that accepts both http(s):// URLs and data: URIs
const imageUrlSchema = z.string().refine(
  (val) => {
    // Accept data URIs for images
    if (val.startsWith('data:image/')) {
      return true
    }
    // Accept valid http/https URLs
    try {
      const url = new URL(val)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
      return false
    }
  },
  { message: 'Must be a valid URL (http/https) or a data URI (data:image/...)' }
)

// Custom schema for URLs or data URIs (for materials that may be any file type)
const urlOrDataUriSchema = z.string().refine(
  (val) => {
    // Accept any data URI
    if (val.startsWith('data:')) {
      return true
    }
    // Accept valid http/https URLs
    try {
      const url = new URL(val)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
      return false
    }
  },
  { message: 'Must be a valid URL (http/https) or a data URI (data:...)' }
)

// Moderation tasks attached to discussion-type slides
const moderationTaskSchema = z
  .object({
    ask: z.string().max(150),
    additionalInstructions: z.string().max(400).optional(),
  })
  .strict()

// Solo Bot configuration attached to a slide
const soloBotSchema = z
  .object({
    type: z.enum([
      'Solo Moderator Bot',
      'Solo Tutor Bot',
      'Solo Grader Bot',
      'Solo Role Play Bot',
    ]),
    talkTrack: z.string().optional(), // Solo Talk Track
    additionalInstructions: z.string().nullish(),
    voiceId: z.nativeEnum(VoiceOption).nullish(),
  })
  .strict()

// Animated Cards content for slideType 17
const animatedCardItemSchema = z
  .object({
    cardTitle: z.string().max(30),
    cardDescription: z.string().max(200),
  })
  .strict()

const animatedCardsSchema = z
  .object({
    contentTitle: z.string().max(30),
    contentDescription: z.string().max(260),
    // fixture may exceed 100; allow arbitrary length here while keeping other constraints
    contentFooter: z.string(),
    cards: z.array(animatedCardItemSchema),
  })
  .strict()

// Quiz question objects for slides (same structure as pre-quiz)
const importQuizQuestionSchema = z
  .object({
    questionId: z.string(),
    answers: z.array(z.string()).nullable(),
    correct: z.number(),
    explanation: z.string().nullable().optional(),
    order: z.number().nullable().optional(),
    question: z.string(),
    questionType: z.number().default(1), // Default to multipleChoice
  })
  .strict()

// Slide objects coming from import JSON
const importSlideSchema = z
  .object({
    slideId: z.string(),
    slideHeading: z.string().optional(),
    slideGroupTalkTrack: z.string().optional(),
    slideDescription: z.string().nullable().optional(),
    slideDuration: z.number(),
    slideImageAltText: z.string().nullable().optional(),
    slideMarkdown: z.string().nullable().optional(),
    slideName: z.string(),
    globalAssetName: z.string().optional(),
    allowSkipVideo: z.boolean().optional(),
    slideType: z.nativeEnum(SlideType),
    moderationTasks: z.array(moderationTaskSchema).optional(),
    animatedCards: animatedCardsSchema.optional(),
    // Poll slide support (slideType=4). Optional so other slide types still validate
    pollQuestion: z.string().optional(),
    pollAnswers: z.array(z.string()).optional(),
    soloBot: soloBotSchema.optional(),
    // Quiz questions for interactive quiz, group quiz, or end of session slides
    quizQuestions: z.array(importQuizQuestionSchema).optional(),
    // Optional URL or data URI to download and upload as slide image (for slideType 1 basic slides)
    slideImageURL: imageUrlSchema.optional(),
  })
  .strict()

// TypeScript type for Slide, matching importSlideSchema
export type ImportSlideSchema = z.infer<typeof importSlideSchema>

// Exhibit objects from import JSON
const importExhibitSchema = z
  .object({
    exhibitId: z.string(),
    exhibitDescription: z.string().max(200),
    exhibitMarkdown: z.string().nullable().optional(),
    exhibitName: z.string().max(200),
    globalAssetName: z.string().optional(),
    exhibitImageURL: imageUrlSchema.optional(), // URL or data URI to download and upload as exhibit image
    imageAltText: z.string().nullable().optional(),
    SlideId: z.string(),
  })
  .strict()

// Pre-quiz question objects from import JSON
const importPreQuizQuestionSchema = z
  .object({
    questionId: z.string(),
    answers: z.array(z.string()).nullable(),
    correct: z.number(),
    explanation: z.string().nullable().optional(),
    order: z.number().nullable().optional(),
    question: z.string(),
    questionType: z.number().default(1),
  })
  .strict()

// Material objects from import JSON
const importMaterialSchema = z
  .object({
    materialId: z.string(), // Fake ID for mapping
    materialName: z.string(),
    materialDescription: z.string().optional(),
    materialType: z.nativeEnum(SlideDeckMaterialType),
    materialWorkType: z.nativeEnum(SlideDeckMaterialWorkType).optional(),
    viewableByInstructor: z.boolean().optional(), // defaults to true
    viewableByPublic: z.boolean().optional(), // defaults to false
    viewableByStudent: z.boolean().optional(), // defaults to true
    // One of the following sources (mutually exclusive):
    globalAssetName: z.string().optional(), // Reference to existing Media document by title (same pattern as slides/exhibits)
    materialLink: urlOrDataUriSchema.optional(), // URL or data URI to download and re-upload OR store directly for url type
    imageUrl: imageUrlSchema.optional(), // For podcasts: URL to download podcast image
  })
  .strict()

// Rubric objects from import JSON
const importRubricSchema = z
  .object({
    rubricId: z.string(),
    rubric: z.string(),
    rubricDescription: z.string(),
    rubricPrompt: z.string().optional(),
    rubricType: z.nativeEnum(RubricType),
    rubricLabel0: z.string().optional(),
    rubricLabel1: z.string().optional(),
    startSlideId: z.string().optional(), // Fake ID, will be mapped
    slideId: z.string().optional(), // Fake ID, will be mapped (end slide)
  })
  .strict()

// The full importable slide deck JSON schema
export const slideDeckImportSchema = z
  .object({
    slideDeckDescription: z.string(),
    slideDeckDisciplines: z.array(z.string()),
    slideDeckExhibits: z.array(importExhibitSchema),
    slideDeckImageURL: imageUrlSchema.optional(), // Public URL or data URI for cover image
    slideDeckKeyConcepts: z.array(z.string()).nullable().optional(),
    slideDeckLearningObjectives: z.array(z.string()),
    slideDeckMaterials: z.array(importMaterialSchema).optional(), // Materials to import
    slideDeckName: z.string(),
    slideDeckPhraseSet: z.array(z.string()),
    slideDeckPreQuizQuestions: z.array(importPreQuizQuestionSchema),
    slideDeckRubrics: z.array(importRubricSchema).optional(),
    slideDeckTeaser: z.string(),
    slideDeckType: z.number(),
    slides: z.array(importSlideSchema),
    primaryDesignerUserId: z.string().optional(),
    designedForInstructorUserId: z.string().optional(),
  })
  .strict()

export type SlideDeckImportInput = z.infer<typeof slideDeckImportSchema>

/**
 * Validate that all slide ID references in the import data point to slides that exist.
 * Returns an array of error messages for any missing references.
 */
function validateSlideIdReferences(data: SlideDeckImportInput): string[] {
  const errors: string[] = []

  // Collect all valid slide IDs from the slides array
  const validSlideIds = new Set(data.slides.map((s) => s.slideId))

  // Check exhibit SlideId references
  for (const exhibit of data.slideDeckExhibits) {
    if (!validSlideIds.has(exhibit.SlideId)) {
      errors.push(
        `Exhibit "${exhibit.exhibitName}" (${exhibit.exhibitId}) references non-existent slide ID: ${exhibit.SlideId}`
      )
    }
  }

  // Check rubric slide references
  if (data.slideDeckRubrics) {
    for (const rubric of data.slideDeckRubrics) {
      if (rubric.startSlideId && !validSlideIds.has(rubric.startSlideId)) {
        errors.push(
          `Rubric "${rubric.rubric}" (${rubric.rubricId}) references non-existent startSlideId: ${rubric.startSlideId}`
        )
      }
      if (rubric.slideId && !validSlideIds.has(rubric.slideId)) {
        errors.push(
          `Rubric "${rubric.rubric}" (${rubric.rubricId}) references non-existent slideId: ${rubric.slideId}`
        )
      }
    }
  }

  return errors
}

/**
 * Validate a raw JSON string against the composite SlideDeck import schema.
 * Returns the parsed object and no errors if valid, else returns a list of errors.
 */
export function validateSlideDeckImportJson(
  raw: string
):
  | { output: SlideDeckImportInput; errors: undefined }
  | { output: undefined; errors: string[] } {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch (e) {
    return {
      errors: ['Invalid JSON: unable to parse input string'],
      output: undefined,
    }
  }
  const { error, data } = slideDeckImportSchema.safeParse(parsed)

  // parse zod error into readable strings
  if (error) {
    console.error('Slide deck import JSON validation error', error)
    // if path exists prepend, else return just message
    const errors = error.errors.map((e) =>
      e.path.length ? `${e.path}: ${e.message}` : e.message
    )
    return { errors, output: undefined }
  }

  // Validate slide ID references before allowing import
  const referenceErrors = validateSlideIdReferences(data)
  if (referenceErrors.length > 0) {
    console.error(
      'Slide deck import slide ID reference errors',
      referenceErrors
    )
    return { errors: referenceErrors, output: undefined }
  }

  return { output: data, errors: undefined }
}
